/**
 * Locked and mostly-locked ECH blocks (audit rows 7 + 8).
 *
 * Port of the monolith's read-only card, index.html L13495–13573:
 *
 *   ONE GREY SENTENCE — `def.lockNote` → a per-block `_lockSay` override → a
 *   generic fallback. The card, the padlock, the repeated block name and the
 *   Content Builder ID were all restating what the panel header and the canvas
 *   already say (2026-08-18, option A off Jack's mockup). The only thing a
 *   division needs is WHY they can't type here.
 *
 *   THEN THE VALUES — "should still show the info/url and etc on the side as
 *   reference just not editable" (Jack). Read out of the block's OWN markup
 *   (`m.data.__html`), so it is always what will actually send: the image with
 *   its real pixel size, and up to five distinct https: links.
 */
import { urlTailText } from "./echUrl";
import { echByName } from "./echRegistry";

/**
 * ⛔ `ECH_MOSTLY_LOCKED` IS GONE — THE STATE IS DERIVED (2026-08-20).
 *
 * It was a hand-maintained list naming one block, standing in for a rule that the block
 * already states about itself: a block whose ONLY declared field is a URL *is* mostly
 * locked. Naming it in this repo meant Layne could ship another URL-only block and the
 * dashboard would quietly get it wrong until someone remembered this table existed —
 * exactly the class of hard-coding Jack asked to remove ("make sure we're not updating
 * anything that should really be updated in the SFMC content builder").
 *
 * Behaviour-preserving, measured against the live pack before the change: of the 29 ECH
 * content blocks, exactly ONE declares a single field of type `url`
 * (`ECH_ContactUsDefault.ContactUrl`) and none declares a single field of any other type.
 * So the derived rule catches precisely what the table named, and nothing else.
 *
 * Kept as a named export for the monolith parity trail; nothing reads the old table.
 */

/** monolith `__echMostlyLocked` (L11180), now read off the block's own guardrails. */
export function echMostlyLocked(echName: string): boolean {
  try {
    const meta = echByName(String(echName || ""));
    if (!meta) return false;
    const keys = Object.keys(meta.declared || {}).filter((k) => k.indexOf("__") !== 0);
    if (keys.length !== 1) return false;
    return String((meta.declared[keys[0]] || {}).type || "").toLowerCase() === "url";
  } catch { return false; }
}

/**
 * ⛔ `ECH_LOCK_SAY` IS DELETED (2026-08-20, per Jack: *"make sure we're not updating
 * anything that should really be updated in the SFMC content builder… focus solely on
 * the dashboard role"*).
 *
 * It held exactly one entry — a sentence WE wrote about ECH_ContactUsDefault ("Set
 * automatically from the division code on Step 1 — only the link is editable"). Checked
 * against the live pull: that block's guardrail `lock` field is **null**, so the
 * dashboard was putting words in Layne's mouth about how her block behaves, and would
 * have kept asserting them after she changed it.
 *
 * The dashboard's role is to PRESENT a block's declared rules, not to author them. If a
 * block needs a sentence explaining what a division may change, that sentence belongs in
 * its `@GUARDRAILS` `lock` — which `echLockSentence` already prefers. Do not reintroduce
 * this table; add the line in Content Builder instead. Full inventory of what else is
 * still hard-coded here: `ech/spec/HARDCODE-FLAGS.md`.
 */
export const ECH_LOCK_GENERIC = "This block can't be edited here.";

/**
 * monolith `_why` (L13513) — the BLOCK'S OWN `lock` line, else the generic fallback.
 *
 * The generic line is a statement of fact about the panel (there is nothing to type),
 * not a claim about the block's purpose, which is why it is safe to keep hard-coded.
 */
export function echLockSentence(lockNote: string | undefined, echName: string): string {
  void echName; /* kept in the signature: callers pass it, and a block-specific line
                   may legitimately come back — but only from the block itself */
  return String(lockNote || "") || ECH_LOCK_GENERIC;
}

/**
 * THE LINK THE BLOCK WILL ACTUALLY SEND WITH, read out of its own markup.
 *
 * Exists so a "mostly locked" block can tell the truth in ONE place instead of three.
 * The Contact Us panel showed an apparently-empty URL field, a sentence we invented, an
 * image row, and a separate "Links to" row carrying a real resolved address — four
 * elements for one fact, and the field and the row appeared to contradict each other.
 *
 * They never did. `echIscPatch` swaps in the division's own Contact Us layoutblock,
 * which carries that division's real `/contact` href, and `ContactUrl`'s declared
 * default is the bare `"https://"` placeholder — correctly read as "not overridden", so
 * the division's own link stands. Surfacing that link as the field's PLACEHOLDER makes
 * the field self-explanatory: empty means the division's link is used, typing overrides
 * it. Nothing is authored here — the value comes from the block's markup.
 */
export function echEffectiveLink(html: unknown): string {
  const h = String(html ?? "");
  try {
    const re = /<a[^>]+href="([^"]+)"/gi;
    let a: RegExpExecArray | null;
    while ((a = re.exec(h))) {
      const u = a[1];
      /* a host is required, not just a scheme — the same one-character-tighter test
         `echIscHtml` uses, so the shipped `https://` placeholder is not mistaken for
         a real destination */
      if (/^https?:\/\/[^\s/]/i.test(u)) return u;
    }
  } catch { /* a malformed block must not take the panel down */ }
  return "";
}

/** One read-only reference row under a locked block. */
export interface EchRefRow {
  /** "Image" or "Links to" */
  key: string;
  /** "80 × 53" on the image row, "" elsewhere — rendered as a dim .ech-refdim */
  dim: string;
  /** the FULL url — what Copy/Open act on; .ech-refv shows the shortened form */
  url: string;
  /** true on a `Links to` row, i.e. the only rows that get Copy/Open */
  link: boolean;
}

/**
 * Read the reference rows out of a block's own markup (monolith L13525–13556).
 *
 * Up to five distinct `https:` links. INTENTIONAL DIVERGENCE from the monolith
 * (Jack, 2026-08-21): the monolith leads with an Image row; that row is dropped
 * here as viewer noise — see the comment inside.
 *
 * ROWS ARE FILTERED ON THE **RENDERED** VALUE, NOT THE RAW ONE. The block ships
 * a bare `https://` placeholder href: truthy, passes `^https?:`, and shortens to
 * the empty string — which is what produced an empty "Links to" row on the live
 * panel (2026-08-18).
 */
export function echRefRows(html: unknown): EchRefRow[] {
  const h = String(html ?? "");
  const out: EchRefRow[] = [];
  try {
    /* The "Image · 40 × 41 · image.news.lennar.com/…" row was DROPPED per Jack
       (2026-08-21): "unnecessary for the front end viewer" — a requester never
       acts on which locked asset a block uses. `Links to` rows stay: those are
       what he asked to make copy/testable on 2026-08-19. The `out.length < 5`
       cap below now counts links only. */
    const seen: Record<string, 1> = {};
    const re = /<a[^>]+href="([^"]+)"/gi;
    let a: RegExpExecArray | null;
    while ((a = re.exec(h)) && out.length < 5) {
      const u = a[1];
      if (!/^https?:/i.test(u) || /^https:\/\/#/.test(u) || seen[u]) continue;
      seen[u] = 1;
      out.push({ key: "Links to", dim: "", url: u, link: true });
    }
  } catch { /* a malformed block must not take the panel down */ }
  return out.filter((r) => urlTailText(r.url));
}
